import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Avatar,
  Button,
  useTheme,
  useMediaQuery,
  Link,
} from "@mui/material";
import venderService from "@/service/vender.service";
import defaultProfilePic from "@/assets/default-icon.jpg";

export default function ReviewsSection({ VendorDetail }) {
  const [reviews, setReviews] = useState([]);
  const [visibleReviews, setVisibleReviews] = useState([]);
  const [isExpanded, setIsExpanded] = useState(false);

  const theme = useTheme();
  const isNarrow = useMediaQuery("(max-width:991px)");

  // Fetch and initialize reviews
  useEffect(() => {
    async function fetch() {
      if (!VendorDetail?._id) return;
      const data = await venderService.fetchReviews(VendorDetail._id);
      const all = (data?.reviews || []).sort((a, b) => {
        // google reviews first
        if (a.source === "google" && b.source !== "google") return -1;
        if (a.source !== "google" && b.source === "google") return 1;
        return 0;
      });
      setReviews(all);
      setVisibleReviews(all.slice(0, 2));
    }
    fetch();
  }, [VendorDetail]);

  // Render star rating as text
  const renderStars = (rating) => {
    const full = "★".repeat(rating);
    const empty = "☆".repeat(5 - rating);
    return `${full}${empty}`;
  };

  // Toggle between showing 2 and all reviews
  const handleToggle = () => {
    if (isExpanded) {
      setVisibleReviews(reviews.slice(0, 2));
    } else {
      setVisibleReviews(reviews);
    }
    setIsExpanded(!isExpanded);
  };

  if (!reviews.length) return null;

  return (
    <Box
      component="section"
      sx={{
        bgcolor: "#fbfcff",
        border: "1px solid #d9d9d9",
        display: "flex",
        flexDirection: "column",
        mt: 1,
        p: isNarrow ? 2 : "10px 38px",
        fontFamily: `"Poppins", ${theme.typography.fontFamily}`,
      }}
    >
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "stretch",
          maxWidth: "100%",
          fontSize: 14,
          fontWeight: 400,
          lineHeight: "18px",
        }}
      >
        <Typography variant="h6" fontWeight={600} gutterBottom>
          Client Reviews
        </Typography>

        {visibleReviews.map((review, idx) => (
          <Box
            key={idx}
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 2,
              mt: idx === 0 ? 0 : 1.5,
            }}
          >
            <Avatar
              src={
                review.source === "google"
                  ? review.profile_photo_url || defaultProfilePic
                  : review.customer?.profile_picture?.file_url || defaultProfilePic
              }
              alt="Reviewer"
              sx={{ width: 32, height: 32 }}
            />
            <Typography component="div" sx={{ flex: 1 }}>
              “{review.comment || "No comment"}”<br />
              {review.source === "google" ? (
                <Box
                  component="span"
                  sx={{ fontWeight: 500, color: "#5e5acf", mr: 1 }}
                >
                  –{" "}
                  <Link
                    href={review.author_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    sx={{ color: "#5e5acf", fontWeight: 500 }}
                  >
                    {review.author_name}
                  </Link>
                </Box>
              ) : (
                <Box
                  component="span"
                  sx={{ fontWeight: 500, color: "#5e5acf", mr: 1 }}
                >
                  – {review.customer?.first_name} {review.customer?.last_name}
                </Box>
              )}
              <Box component="span">{renderStars(review.rating)}</Box>
            </Typography>
          </Box>
        ))}

        {reviews.length > 2 && (
          <Button
            variant="text"
            onClick={handleToggle}
            sx={{
              color: "#5e5acf",
              alignSelf: "flex-end",
              mt: isNarrow ? 1 : 2,
              fontSize: 16,
              fontWeight: 600,
              textTransform: "none",
              p: 0,
            }}
          >
            {isExpanded ? "See Less" : "See More"}
          </Button>
        )}
      </Box>
    </Box>
  );
}
